/**
 * Trips API
 * 
 * Handles all trip-related API calls including:
 * - Trip CRUD operations
 * - Trip statistics (counts, earnings)
 */

import api from './client';

export const tripsApi = {
  /**
   * Get trip count for today
   * @returns {Promise<number>} Number of trips today
   */
  async getCountToday() {
    try {
      const response = await api.get('/api/trips/count_today');
      return response.data.count || 0;
    } catch (error) {
      console.error('Error fetching trip count:', error);
      return 0;
    }
  },

  /**
   * Get trip count for yesterday
   * @returns {Promise<number>} Number of trips yesterday
   */
  async getCountYesterday() {
    try {
      const response = await api.get('/api/trips/count_yesterday');
      return response.data.count || 0;
    } catch (error) {
      console.error('Error fetching trip count:', error);
      return 0;
    }
  },

  /**
   * Get earnings for today
   * @param {boolean} personal - Whether to get personal earnings only
   * @returns {Promise<number>} Earnings today
   */
  async getEarningsToday(personal = false) {
    try {
      const params = personal ? { personal: 'true' } : {};
      const response = await api.get('/api/trips/earnings_today', { params });
      return response.data.earnings || 0;
    } catch (error) {
      console.error('Error fetching earnings:', error);
      return 0;
    }
  },

  /**
   * Get earnings for this month
   * @param {boolean} personal - Whether to get personal earnings only
   * @returns {Promise<number>} Earnings this month
   */
  async getEarningsMonth(personal = false) {
    try {
      const params = personal ? { personal: 'true' } : {};
      const response = await api.get('/api/trips/earnings_month', { params });
      return response.data.earnings || 0;
    } catch (error) {
      console.error('Error fetching earnings:', error);
      return 0;
    }
  },

  /**
   * Create a new trip
   * @param {Object} tripData - Trip data (from_location, to_location, pax, etc.)
   * @returns {Promise<Object>} Created trip data
   */
  async createTrip(tripData) {
    const response = await api.post('/api/create_trip', tripData);
    return response.data;
  },

  /**
   * Get trip by ID
   * @param {number} tripId - Trip ID
   * @returns {Promise<Object>} Trip data
   */
  async getTrip(tripId) {
    const response = await api.get(`/api/trips/${tripId}`);
    return response.data;
  },

  /**
   * Update trip
   * @param {number} tripId - Trip ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} Updated trip data
   */
  async updateTrip(tripId, updateData) {
    const response = await api.patch(`/api/trips/${tripId}`, updateData);
    return response.data;
  },

  /**
   * Get all available trips (for drivers)
   * @returns {Promise<Array>} List of available trips
   */
  async getAvailableTrips() {
    const response = await api.get('/api/trips');
    return response.data;
  },

  /**
   * Get past 24-hour trip history (Rider & Driver)
   * @returns {Promise<Array>} List of completed trips
   */
  async getTripHistory() {
    const response = await api.get('/api/trips/history');
    return response.data;
  },
};

export default tripsApi;
